import { Component } from '@angular/core';
import { Validators } from '@angular/forms';
import { SimpleSearchComponent } from 'src/app/base/components/abstract/simple/simple-search.component';
import { DataAction } from 'src/app/base/enum/data-action.enum';
import { DialogReturnData } from 'src/app/base/models/dialog-return-data.model';
import { Option } from 'src/app/base/models/option.model';
import { TableColumn } from 'src/app/base/models/table-column.model';
import { takeUntil, Subject, switchMap, filter, take, lastValueFrom } from 'rxjs';
import { StockSummaryService } from '../../services/stock-summary.service';
import { AccountService } from '../../services/account.service';
import { CommonService } from '../../services/common.service';
import { SalesCostLookupDialogComponent, LookupDialogType } from '../sales-cost-lookup-dialog/sales-cost-lookup-dialog.component';
import { AuthService } from 'src/app/core/services/auth.service';

interface ItemOption extends Option {
  description?: string;
  brand?: string;
}

@Component({
  selector: 'app-sales-cost-inquiry',
  templateUrl: './sales-cost-inquiry.component.html',
  styleUrls: ['./sales-cost-inquiry.component.scss'],
})
export class SalesCostInquiryComponent extends SimpleSearchComponent {
  override rowDataKeyName: string = 'currency';
  override rowDataDeleteHintFieldName: string = 'currency';
  override componentClassName: string = 'SalesCostInquiryComponent';

  override showActions: boolean = false;
  override showNoDataFoundMessage: boolean = false;

  /** 從 itemAll API 取得的 inventoryItemId，供 doQuery 使用 */
  private resolvedInventoryItemId: number = 0;

  private resolvedDefaultOu: Option | null = null;

  // OU AutoComplete
  ouSuggestions: Option[] = [];
  private readonly ouSubject$ = new Subject<string | undefined>();

  // Brand AutoComplete
  brandSuggestions: Option[] = [];
  private readonly brandSubject$ = new Subject<string | undefined>();

  // Item Number AutoComplete
  itemSuggestions: ItemOption[] = [];
  private readonly itemSubject$ = new Subject<string | undefined>();

  // Cust AutoComplete
  custSuggestions: Option[] = [];
  private readonly custSubject$ = new Subject<string>();

  queryId: string = '';

  constructor(
    private readonly stockSummaryService: StockSummaryService,
    private readonly accountService: AccountService,
    private readonly commonService: CommonService,
    private readonly authService: AuthService
  ) {
    super();
  }

  override async beforeQuery(): Promise<boolean> {
    return true;
  }

  override async doQuery(): Promise<boolean> {
    const ouValue = this.formControl('ouId').value;
    const brandValue = this.formControl('brand').value;
    const custValue = this.formControl('cust').value;
    const itemValue = this.formControl('itemNumber').value;
    const area: string = this.formControl('area').value ?? '';
    const subArea: string = this.formControl('subArea').value ?? '';
    const endCustomer = (this.formControl('endCustomer').value as string) || undefined;
    const invOrg = (this.formControl('invOrg').value as string) || undefined;

    const ouId = ouValue?.id ? Number(ouValue.id) : 0;
    const brand: string = brandValue?.value ?? '';
    const custId = custValue?.id ? Number(custValue.id) : 0;
    const parsedItemId = Number(itemValue?.id);
    const inventoryItemId = (itemValue?.id != null && !isNaN(parsedItemId)) ? parsedItemId : 0;
    const empNo: string = this.authService.getUser()?.username ?? '';
    const uuid = crypto.randomUUID();

    try {
      const res = await lastValueFrom(
        this.commonService.getSalesCostInqData({
          area,
          brand,
          custId,
          empNo,
          ...(endCustomer ? { endCustomer } : {}),
          inventoryItemId: inventoryItemId,
          ouId,
          ...(invOrg ? { organizationCode: invOrg } : {}),
          queryFindArea: subArea,
          uuid,
        })
      );
      this.queryId = res.queryId ?? '';
      this.detailDatas = res.responseData ?? [];
    } catch {
      this.detailDatas = [];
    }
    return true;
  }

  override afterSelectedRowDataCountChange(selectedDataCount: number): void {
    return;
  }
  override afterRowDataCountChange(rowDataCount: number): void {
    return;
  }
  override initExtraDetailMenu(): void {
    return;
  }
  override setExtraDetailMenuStatus(): void {
    return;
  }
  override initTableColumns(): void {
    this.tableColumns = [
      {
        header: 'Currency',
        field: 'currency',
        sortable: true,
      },
      {
        header: 'field.worst-selling-price',
        field: 'sellingPrice',
        sortable: true,
        alignRight: true,
      },
      {
        header: 'field.worst-sales-gp',
        field: 'salesGp',
        sortable: true,
        alignRight: true,
      },
      {
        header: 'field.sales-cost',
        field: 'salesCost',
        sortable: true,
        numberField: true,
      },
      {
        header: 'field.valid-from',
        field: 'validFrom',
        sortable: true,
      },
      {
        header: 'field.valid-to',
        field: 'validTo',
        sortable: true,
      },
      {
        header: 'field.qty-balance',
        field: 'quantityUsed',
        sortable: true,
        numberField: true,
      },
    ];
  }
  override initDialogConfig(dataAction: DataAction): void {
    return;
  }
  override initExtraRowActionMenu(): void {
    return;
  }
  override setExtraRowActionMenuStatus(): void {
    return;
  }
  override doExtraDetailDialogClose(returnData: DialogReturnData): void {
    return;
  }
  override async doRowActionDelete(): Promise<boolean> {
    return true;
  }
  override afterDetailTabActiveItemChange(): void {
    return;
  }
  override initFormGroup(): void {
    this.formGroup = this.formBuilder.nonNullable.group({
      ouId: ['', Validators.required],
      brand: ['', Validators.required],
      cust: ['', Validators.required],
      itemNumber: ['', Validators.required],
      itemDesc: [{ value: '', disabled: true }],
      area: [''],
      subArea: ['', Validators.required],
      endCustomer: '',
      invOrg: '',
    });
  }
  override initStaticOptions(): void {
    return;
  }
  override async initFormData(): Promise<any> {
    const data = this.dynamicDialogConfig.data;
    if (!data?.ou) {
      const orgs = await lastValueFrom(this.stockSummaryService.getOrgs().pipe(take(1))).catch(() => []);
      this.resolvedDefaultOu = orgs[0] ?? null;
    }
    if (data?.itemNumber) {
      const items = await lastValueFrom(
        this.commonService.getItemsWithDesc(data.itemNumber, data.inventoryItemId).pipe(take(1))
      ).catch(() => []);
      const match = items.find((i: any) => i.value === data.itemNumber || i.label === data.itemNumber);
      if (match) {
        this.resolvedInventoryItemId = match.id ? Number(match.id) : 0;
        if (match.description) {
          this.formGroup.patchValue({ itemDesc: match.description });
        }
      }
    }
    return true;
  }

  private loadMockData(): void {
    this.detailDatas = [
      {
        currency: 'USD',
        sellingPrice: '0.0002',
        salesGp: '0.5000%',
        salesCost: 0.0001,
        validFrom: '2026/3/13',
        validTo: '2026/3/13',
        quantityUsed: 0,
      },
    ];
  }
  override afterLangChange(): void {
    return;
  }
  override afterFormDataLoadSuccess(): void {
    const data = this.dynamicDialogConfig.data;

    const patches: any = {};
    if (data?.ou) {
      patches.ouId = data.ou;
    } else if (this.resolvedDefaultOu) {
      patches.ouId = this.resolvedDefaultOu;
    }
    if (data?.brand) {
      patches.brand = { id: data.brand, value: data.brand, label: data.brand };
    }
    if (data?.itemNumber) {
      patches.itemNumber = {
        id: this.resolvedInventoryItemId || data.inventoryItemId || data.itemNumber,
        value: data.itemNumber,
        label: data.itemNumber,
      };
    }

    if (Object.keys(patches).length === 0) return;

    this.formGroup.patchValue(patches);
    this.changeDetectorRef.detectChanges();

    // 預先查詢 Sub Area
    const effectiveOu = data?.ou ?? this.resolvedDefaultOu;
    const ouId: number = effectiveOu?.id ? Number(effectiveOu.id) : 0;
    if (ouId) {
      const empNo: string = this.authService.getUser()?.username ?? '';
      this.commonService.getSubAreas(ouId, empNo).pipe(take(1)).subscribe({
        next: (subAreas) => {
          if (subAreas.length === 1) {
            this.formGroup.patchValue({
              area: subAreas[0].area ?? '',
              subArea: subAreas[0].subArea,
            });
            this.changeDetectorRef.detectChanges();
          }
        },
      });
    }
  }
  override afterFormDataLoadFail(): void {
    return;
  }
  override initFormControlDisabled(): void {
    return;
  }

  /** subArea / invOrg：需先填入 OU 才可操作，填入後開啟 Lookup Dialog */
  openSubAreaLookup(): void {
    if (!this.formControl('ouId').value) {
      this.systemMessageService.warn('message.validation.ou-required-first');
      return;
    }
    this.openLookupDialog('subArea', (result) => {
      this.formGroup.patchValue({
        area: result.area ?? '',
        subArea: result.subArea,
      });
    });
  }

  /** endCustomer：需先填入 OU + Brand 才可操作，填入後開啟 Lookup Dialog */
  openEndCustomerLookup(): void {
    if (!this.formControl('ouId').value || !this.formControl('brand').value) {
      this.systemMessageService.warn('message.validation.ou-and-brand-required-first');
      return;
    }
    this.openLookupDialog('endCustomer', (result) => {
      this.formGroup.patchValue({ endCustomer: result.endCustomer });
    });
  }

  clearSubArea(): void {
    this.formGroup.patchValue({ area: '', subArea: '' });
  }

  clearEndCustomer(): void {
    this.formGroup.patchValue({ endCustomer: '' });
  }

  clearInvOrg(): void {
    this.formGroup.patchValue({ invOrg: '' });
  }

  /** invOrg：需先填入 OU 才可操作，填入後開啟 Lookup Dialog */
  openInvOrgLookup(): void {
    if (!this.formControl('ouId').value) {
      this.systemMessageService.warn('message.validation.ou-required-first');
      return;
    }
    this.openLookupDialog('invOrg', (result) => {
      this.formGroup.patchValue({ invOrg: result.organizationCode });
    });
  }

  private openLookupDialog(type: LookupDialogType, onSelect: (result: any) => void): void {
    const ouId: number = this.formControl('ouId').value?.id
      ? Number(this.formControl('ouId').value.id)
      : (this.dynamicDialogConfig?.data?.ou?.id ? Number(this.dynamicDialogConfig.data.ou.id) : 0);
    const brand: string = this.formControl('brand').value?.value ?? '';
    const empNo: string = this.authService.getUser()?.username ?? '';
    const headerKey = type === 'subArea' ? 'field.sub-area'
      : type === 'endCustomer' ? 'field.end-customer'
        : 'field.inv-org';
    const ref = this.dialogService.open(SalesCostLookupDialogComponent, {
      modal: true,
      draggable: true,
      resizable: true,
      header: this.translateService.instant(headerKey),
      width: 'min(680px, 95vw)',
      contentStyle: { padding: '20px' },
      data: { type, ouId, brand, empNo },
    });
    ref.onClose.pipe(take(1)).subscribe((result: any) => {
      if (result) {
        onSelect(result);
      }
    });
  }

  searchOuSuggestions(event: { query: any }): void {
    if (!this.ouSubject$.observed) {
      this.ouSubject$
        .pipe(
          switchMap((keyword) => this.stockSummaryService.getOrgs(keyword)),
          takeUntil(this._destroying$)
        )
        .subscribe((data) => {
          this.ouSuggestions = data;
        });
    }
    this.ouSubject$.next(event.query);
  }

  searchBrandSuggestions(event: { query: any }): void {
    if (!this.brandSubject$.observed) {
      this.brandSubject$
        .pipe(
          switchMap((keyword) => this.accountService.getBrands(keyword)),
          takeUntil(this._destroying$)
        )
        .subscribe((data) => {
          this.brandSuggestions = data;
        });
    }
    this.brandSubject$.next(event.query);
  }

  searchItemSuggestions(event: { query: any }): void {
    if (!this.itemSubject$.observed) {
      this.itemSubject$
        .pipe(
          filter(Boolean),
          switchMap((keyword: string) => this.commonService.getItemsWithDesc(keyword)),
          takeUntil(this._destroying$)
        )
        .subscribe({
          next: (data) => {
            this.itemSuggestions = data;
          },
          error: () => {
            this.itemSuggestions = [];
          },
        });
    }
    this.itemSubject$.next(event.query);
  }

  searchCustSuggestions(event: { query: any }): void {
    if (!this.custSubject$.observed) {
      this.custSubject$
        .pipe(
          filter(Boolean),
          switchMap((keyword: string) => this.commonService.getCustomersByNumAndName(keyword)),
          takeUntil(this._destroying$)
        )
        .subscribe({
          next: (data) => {
            this.custSuggestions = data;
          },
          error: () => {
            this.custSuggestions = [];
          },
        });
    }
    this.custSubject$.next(event.query);
  }

  onItemSelect(event: { value: ItemOption }): void {
    const selected = event.value;
    if (selected?.description) {
      this.formGroup.patchValue({ itemDesc: selected.description });
    } else {
      this.formGroup.patchValue({ itemDesc: '' });
    }
    if (selected?.brand) {
      const brand = selected.brand;
      this.formGroup.patchValue({ brand: { id: brand, value: brand, label: brand } });
    }
    this.autoCompleteCompleted('autoCompleteCode');
  }

  onItemClear(): void {
    this.formGroup.patchValue({ itemDesc: '' });
    this.autoCompleteCleared('autoCompleteCode');
  }
}
